import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { useSubscriptionPackages } from '../../queries/subscriptionPackages';
import { useSchedulePlanChange } from '../../queries/userSubscriptions';

interface SchedulePlanChangeDialogProps {
  subscriptionId: number;
  currentPackageCode: string;
  onClose: () => void;
}

export function SchedulePlanChangeDialog({ subscriptionId, currentPackageCode, onClose }: SchedulePlanChangeDialogProps) {
  const { data: packages = [] } = useSubscriptionPackages();
  const changeablePackages = packages.filter((p) => p.is_active && p.billing_model === 'subscription');

  const [packageCode, setPackageCode] = useState(currentPackageCode);
  const [billingCycle, setBillingCycle] = useState<'weekly' | 'monthly'>('weekly');
  const [error, setError] = useState<string | null>(null);
  const schedule = useSchedulePlanChange(subscriptionId);

  const handleConfirm = () => {
    setError(null);
    schedule.mutate(
      { newPackageCode: packageCode, newBillingCycle: billingCycle },
      { onSuccess: onClose, onError: (err) => setError(extractErrorMessage(err, 'Scheduling the plan change failed.')) },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">Change plan</Dialog.Title>
          <p className="mt-1 text-sm text-muted-foreground">
            Takes effect at the next renewal. No proration, no charge today.
          </p>

          <div className="mt-4 space-y-3">
            <label className="block text-sm">
              New package
              <select
                className="mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
                value={packageCode}
                onChange={(e) => setPackageCode(e.target.value)}
              >
                {changeablePackages.map((p) => (
                  <option key={p.package_code} value={p.package_code}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              Billing cycle
              <select
                className="mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
                value={billingCycle}
                onChange={(e) => setBillingCycle(e.target.value as 'weekly' | 'monthly')}
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </label>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={schedule.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {schedule.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Schedule change
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
